import { Component, Input, ViewChild, inject } from "@angular/core";
import { MoiTypes } from "@enums/moi/MoiTypes";
import { LinkedMoiOverviewComponent } from "@app/moi/moi-linked-overview/moi-linked-overview.component";
import { LinkableComponent } from "@app/shared/linkable/linkable.component";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { ControlModel } from "@app/controls/models/ControlModel";
import { IHasGuid } from "@app/shared/interfaces/IHasGuid";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { Observable } from "rxjs";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { MoiDataService } from "@app/moi/services/moi-data.service";
import { ControlDetailComponent } from "../control-detail.component";
import { ControlPermissionModel } from "@app/controls/models/ControlPermissionsModel";
import { LinkModel } from "@models/generic/LinkModel";
import { TabComponentHelper } from "@app/shared/helpers/tab-component-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";

@Component({
    selector: "control-mois",
    templateUrl: "./control-mois.component.html",
    styleUrls: ["./control-mois.component.scss"],
    imports: [LinkedMoiOverviewComponent, LinkableComponent],
})
export class ControlMoiComponent {
    private _controlDS = inject(ControlDataService);
    private _toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _translation = injectTranslation(scope);
    private _moiDS = inject(MoiDataService);
    private _promptService = inject(CerrixPromptService);

    @Input() controlModel: ControlModel;
    @Input() moiType: MoiTypes;
    @Input() parentGuid: string;
    @Input() permissions: ControlPermissionModel;

    @ViewChild("linkedControlMoisComponent")
    linkedControlMoisComponent: LinkedMoiOverviewComponent;
    @ViewChild("linkedImMoi") linkedImMoi: LinkableComponent;

    isLoading = true;
    // Link IM MOIS
    displayValueProp = "name";
    linked: LinkModel[] = [];
    unlinked: LinkModel[] = [];
    disabled: false;
    hideActions: true;
    hideFilter: false;
    allowAddNew = false;

    hasAccessChecks = {};
    accessCheckInProgress = false;
    cdc: ControlDetailComponent;

    async load(refresh: boolean, cdc: ControlDetailComponent): Promise<void> {
        this.linkedControlMoisComponent.load(refresh);
        this.cdc = cdc;

        if (!this.controlModel.Organization) {
            await this._translation.ready();
            this._toastr.warning(this._translation.t("controlMois.selectOrgFirst"));
            this.isLoading = false;
            return;
        }

        this._controlDS
            .getLinkableMois(this.controlModel.Guid, this.controlModel.Organization)
            .subscribe((data) => {
                this.loadData(data, this.controlModel.MoiIdList);
                this.isLoading = false;
            });
    }

    onLinkedItemsChanged(linked: LinkModel[]): void {
        const newMoisList = linked.map((x) => x.id);
        this.controlModel.MoiIdList = newMoisList.sort((a, b) => a - b);

        const isChanged = this.controlModel.MoiIdList.isDifferent(
            this.cdc.detailMain.backupModel.MoiIdList
        );
        TabComponentHelper.toggleTabDirty(this.cdc.tab, "menuItemMoi", isChanged);
    }

    private loadData(mois: LinkModel[], linkedMois: number[]): void {
        const linked: LinkModel[] = [];
        const unlinked: LinkModel[] = [];

        if (mois) {
            mois.forEach((m) => {
                if (linkedMois && linkedMois.find((x) => x === m.id)) {
                    linked.push(m);
                } else {
                    unlinked.pushIfNotExists(m);
                }
            });
        }

        this.linked = linked;
        this.unlinked = unlinked;
    }

    async openItem(item: IHasGuid): Promise<void> {
        const hasAccess = this.hasAccessChecks[item.guid];
        if (hasAccess == null && !this.accessCheckInProgress) {
            this.accessCheckInProgress = true;
            await this._translation.ready();

            const loadingPrompt = this._promptService.loader(this._translation.t("controlMois.checkingRights"));

            this.hasAccessToItem(item.guid)
                .pipe(finalize(() => loadingPrompt.close()))
                .subscribe((x) => {
                    this.hasAccessChecks[item.guid] = x;

                    this.openTab(item, x);
                    this.accessCheckInProgress = false;
                });
        } else {
            this.openTab(item, hasAccess);
        }
    }

    hasAccessToItem(guid: string): Observable<boolean> {
        return this._moiDS.hasAccessToMoi(guid);
    }

    async openTab(item: IHasGuid, hasAccess: boolean): Promise<void> {
        if (hasAccess === true) {
            this._tabService.generateTab(
                this._pages.MoiDetail,
                item.guid,
                `(M) ${item[this.displayValueProp]}`
            );
        } else {
            await this._translation.ready();
            this._toastr.warning(this._translation.t("controlMois.notEnoughRightsMoi"));
        }
    }
}
